import { useMemo } from "react";
import {
  useGetClientQuery,
  useListStateInfoQuery,
} from "features/trading/accounts";

/**
 * State tax rate (percent) used by the strategy report, plus the state it
 * comes from.
 *
 * Derived, never entered: the client's own State (set on its Details tab)
 * crossed with that state's rate in Settings (General tab). See
 * useFederalTaxRate for the federal rate. Takes the client id instead of
 * reading the report settings context so it stays usable from anywhere.
 */
export const useStateTaxRate = (clientId: number | null) => {
  const { data: client, isFetching: isFetchingClient } = useGetClientQuery(
    clientId ?? undefined,
    { skip: !clientId },
  );
  const { data: statesData, isLoading: isLoadingStates } =
    useListStateInfoQuery({
      pagination: { page: 0, pageSize: 100 },
      sorting: [{ field: "state_name", sort: "asc" }],
    });

  const clientState = useMemo(() => {
    // getClient's data outlives a change of client until the new one lands,
    // so only trust it for the client asked about.
    if (!clientId || client?.id !== clientId || client.state == null) {
      return null;
    }
    return (
      statesData?.results?.find((stateInfo) => stateInfo.id === client.state) ??
      null
    );
  }, [client, clientId, statesData]);

  const stateTaxRate = useMemo(() => {
    // DecimalField over the wire, so it arrives as a string.
    const parsedRate = Number(clientState?.tax_rate);
    if (clientState?.tax_rate == null || !Number.isFinite(parsedRate)) {
      return null;
    }
    return parsedRate;
  }, [clientState]);

  return {
    stateTaxRate,
    clientState,
    // While a newly picked client loads, the cached row is still the old
    // one: that is loading, not "no state".
    isLoading:
      isLoadingStates ||
      (!!clientId && (isFetchingClient || client?.id !== clientId)),
  };
};
